import type { FC } from "react";
import React, { useCallback, useEffect, useState } from "react";

import styled from "@emotion/styled";
import { IconPaperclipAttachmentFile } from "src/constants/icons";
import fileSizeString from "src/lib/fileSizeString";
import translate from "src/lib/translate";
import FilesCubit from "src/state/FilesCubit/FilesCubit";
import { fileState } from "src/state/state";

const Attachment = styled.button`
  label: Attachment;
  outline-color: transparent;
  cursor: pointer;
  text-align: left;
  display: grid;
  grid-template-columns: 3rem 1fr;
  margin: 0.5em 0 0;
  background: var(--color-white);
  position: relative;
  align-items: stretch;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid var(--color-gray-light);
  max-width: 22rem;
  margin-left: auto;
  z-index: 1;
  width: 100%;
  transition: background 0.2s ease-in-out;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:hover,
  &:focus-visible,
  &:active {
    background: var(--color-gray-lighter);
  }

  &:first-of-type:not(:last-of-type) {
    margin-bottom: 0;
    border-radius: 0.5rem 0.5rem 0 0;
    --wbr-lb: 0;
  }

  &:last-of-type:not(:first-of-type) {
    margin-top: 0;
    border-top: 0;
    border-radius: 0 0 0.5rem 0.5rem;
    --wbr-lt: 0;
  }

  &:not(:first-of-type):not(:last-of-type) {
    border-radius: 0;
    border-top: 0;
    margin: 0;
    --wbr-lt: 0;
    --wbr-lb: 0;
  }
`;

const Icon = styled.div`
  background: var(--gradient-sunrise);
  color: var(--color-charcoal-60);
  display: grid;
  place-items: center;
  padding: 0.8em 0.5em;
  position: relative;

  svg {
    width: 1.6em;
    height: 1.6em;
    position: relative;
    z-index: 5;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: var(--wbr-lt, 0.4rem) 0 0 var(--wbr-lb, 0.4rem);
    background: var(--color-white);
    opacity: 0.6;
  }
`;

const Content = styled.div`
  padding: 0.3em 1.2em 0.3em 0.6em;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.04em;
  background-color: #ffffff88;
  overflow: hidden;
`;

const Name = styled.div`
  font-weight: 600;
  font-size: 0.9em;
  line-height: 1.2em;
  color: var(--color-charcoal);
  white-space: nowrap;
  padding: 0.1em 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Metadata = styled.div`
  font-size: 0.8em;
  line-height: 1.2em;
  color: var(--color-charcoal-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface FilePreviewProps {
  id?: string;
  name?: string;
}

const FilePreview: FC<FilePreviewProps> = ({ id, name }) => {
  const [loadedMetadata, setLoadedMetadata] = useState<{
    name?: string;
    type?: string;
    size?: number;
  }>({});

  useEffect(() => {
    if (!id) {
      return;
    }

    void fileState.getFileMetadata(id).then((metadata) => {
      if (metadata) {
        setLoadedMetadata({
          name: metadata.filename ?? undefined,
          type: metadata.contentType ?? undefined,
          size: metadata.contentLength ?? undefined
        });
      }
    });
  }, [id]);

  const loadFile = useCallback(() => {
    if (!id) {
      return;
    }
    void FilesCubit.showFileActionSheet(id);
  }, [id]);

  const finalName = name ?? loadedMetadata.name ?? "";

  const sizeString = loadedMetadata.size
    ? fileSizeString(loadedMetadata.size)
    : "";
  const typeString = loadedMetadata.type
    ? translate(`file_type`, {
        context: loadedMetadata.type.replace("/", "_")
      })
    : "";

  const title = finalName.replaceAll("_", " ");

  return (
    <Attachment title={title} onClick={loadFile} className="file-attachment">
      <Icon>
        <IconPaperclipAttachmentFile />
      </Icon>
      <Content>
        <Name>{title}</Name>
        <Metadata>
          {[typeString, sizeString].filter(Boolean).join(" | ")}
        </Metadata>
      </Content>
    </Attachment>
  );
};

export default FilePreview;
